import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useClickAway } from 'react-use';
import { ManualTreeChapter, ManualTreeExcerptColorResult, User } from '../../../../../api/model';
import useContextMenu from '../../../../../hooks/useContextMenu';
import { SectionTree } from './SectionTree';
import { SectionChapterMenuDelete } from './chaptermenu/SectionChapterMenuDelete';
import { SectionChapterMenuMove } from './chaptermenu/SectionChapterMenuMove';
import { SectionChapterMenuTitle } from './chaptermenu/SectionChapterMenuTitle';
import { SectionTreeMenuAdd } from './sectionmenu/SectionTreeMenuAdd';
import { SectionTreeMenuPdfPreview } from './sectionmenu/SectionTreeMenuPdfPreview';
import { SectionSlimMenu } from './slimmenu/SectionSlimMenu';

type ChapterProps = {
  /* eslint @typescript-eslint/no-explicit-any: off */
  /* eslint @typescript-eslint/no-unsafe-call: off */
  productionId: number;
  productionDivisionId: number;
  sectionId: number | null;
  chapter: ManualTreeChapter;
  user: User;
  isEdit: boolean;
  setIsEdit: (isEdit: boolean) => void;
  setSectionId: (sectionId: number) => void;
  onSearch: () => void;
  beforeMovingMyEditSection: boolean | null;
  setBeforeMovingMyEditSection: (beforeMovingMyEditSection: boolean | null) => void;
  initChapterId: number;
  allOpen: boolean;
  allClose: boolean;
  TreeExcerptColor: ManualTreeExcerptColorResult[];
  excerptcolorRefetch: () => void;
  setSecReading: (secReading: boolean) => void;
  secReading: boolean;
};
function SectionChapter({
  productionId,
  productionDivisionId,
  sectionId,
  chapter,
  user,
  isEdit,
  setIsEdit,
  setSectionId,
  onSearch,
  beforeMovingMyEditSection,
  setBeforeMovingMyEditSection,
  initChapterId,
  allOpen,
  allClose,
  TreeExcerptColor,
  excerptcolorRefetch,
  setSecReading,
  secReading,
}: ChapterProps) {
  const [contextOpen, setContextOpen] = useState(false);
  const { points, setPoints } = useContextMenu();

  const ref = useRef(null);
  useClickAway(ref, () => {
    setContextOpen(false);
  });

  /**
   * ブラウザのwindowサイズ取得
   */
  const [contextUp, setContextUp] = useState(false);
  const useWindowSize = (): number[] => {
    const [size, setSize] = useState([0, 0]);
    useLayoutEffect(() => {
      const updateSize = (): void => {
        setSize([window.innerWidth, window.innerHeight]);
      };

      window.addEventListener('resize', updateSize);
      updateSize();

      return () => window.removeEventListener('resize', updateSize);
    }, []);
    return size;
  };
  const [width, height] = useWindowSize();

  const handleContextMenu = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e.preventDefault();
    setContextOpen(true);
  };

  const [isOpen, setIsOpen] = useState(false); // メニュー開閉
  // 手動で開閉
  const handleOpen = () => {
    setIsOpen(!isOpen);
  };

  // 読込み時の開閉
  useEffect(() => {
    if (allClose) {
      setIsOpen(false);
    } else if (allOpen) {
      setIsOpen(true);
    } else {
      setIsOpen(chapter.is_open === true || initChapterId === chapter.chapter_id);
    }
  }, [chapter, initChapterId, allOpen, allClose]);

  // WebOMプレビューメニューの表示／非表示(navi, 翻訳navi 以外でWebOmプレビューを表示)
  const isShowPreviewWebOm = useMemo(
    () => [1, 2, 4, 5, 6, 7, 8, 10, 11, 12, 13, 14].includes(productionDivisionId),
    [productionDivisionId]
  );

  return (
    <li className="tree1" key={`chapter-${String(chapter.chapter_id)}`}>
      <i
        className={`fa-solid ${isOpen ? 'fa-caret-down' : 'fa-caret-right'}`}
        onClick={() => handleOpen()}
        onContextMenu={() => setContextOpen(true)}
        style={{
          cursor: 'pointer',
          color: TreeExcerptColor.find((item) => item.chapter_id === Number(chapter.chapter_id))?.color,
        }}
      />
      <span
        onContextMenu={(event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
          event.preventDefault();
          if (height - event.clientY < 220) {
            setContextUp(true);
            setPoints({ x: event.clientX, y: height - event.clientY + 5 });
          } else {
            setPoints({ x: event.clientX, y: event.clientY + 5 });
          }
          handleContextMenu(event);
        }}
      >
        <span style={{ color: chapter.enabled === 1 ? '' : 'lightgrey', cursor: 'pointer' }}>
          <span style={{ color: '#c21413', fontWeight: 'bold', margin: '0px 4px 0px 0px' }}>1</span>
          {chapter.title}
        </span>
        <ul
          className="mt-popup"
          style={{
            display: contextOpen ? 'block' : '',
            top: contextUp ? '' : `${points.y}px`,
            bottom: contextUp ? `${points.y}px` : '',
            left: `${points.x}px`,
          }}
          ref={ref}
        >
          <SectionChapterMenuTitle chapterId={chapter.chapter_id} productionId={productionId} title={chapter.title} />
          <SectionTreeMenuAdd chapterId={chapter.chapter_id} productionId={productionId} />
          <SectionSlimMenu
            productionId={productionId}
            chapterId={chapter.chapter_id}
            sectionId={undefined}
            level={1}
            title={chapter.title}
            excerptcolorRefetch={excerptcolorRefetch}
          />
          <SectionTreeMenuPdfPreview
            chapterId={chapter.chapter_id}
            sectionId={undefined}
            isShowPreviewWebOm={isShowPreviewWebOm}
          />
          {/* om追補, navi, 翻訳om追補, 翻訳navi */}
          {(productionDivisionId === 2 ||
            productionDivisionId === 3 ||
            productionDivisionId === 8 ||
            productionDivisionId === 9) && (
            <>
              <SectionChapterMenuMove chapterId={chapter.chapter_id} productionId={productionId} />
              <SectionChapterMenuDelete chapterId={chapter.chapter_id} productionId={productionId} />
            </>
          )}
        </ul>
      </span>

      <ul className={isOpen ? 'tree2' : 'treeul2'}>
        {chapter.manual_tree_sections?.map((tree) => (
          <SectionTree
            isEdit={isEdit}
            setIsEdit={setIsEdit}
            productionId={productionId}
            productionDivisionId={productionDivisionId}
            selectedSectionId={sectionId}
            tree={tree}
            user={user}
            setSectionId={setSectionId}
            onSearch={onSearch}
            key={`tree-${String(tree.chapter_id)}-${String(tree.section_id)}-${String(tree.level)}-${String(
              tree.l2_section_id
            )}-${String(tree.l3_section_id)}`}
            beforeMovingMyEditSection={beforeMovingMyEditSection}
            setBeforeMovingMyEditSection={setBeforeMovingMyEditSection}
            allOpen={allOpen}
            allClose={allClose}
            TreeExcerptColor={TreeExcerptColor}
            excerptcolorRefetch={excerptcolorRefetch}
            setSecReading={setSecReading}
            secReading={secReading}
          />
        ))}
      </ul>
    </li>
  );
}
export { SectionChapter };
